import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collegeService, placementDriveService } from '../services/api';
import './CollegeDetails.css';

function CollegeHeaderImage({ logo, name }) {
  const [imageError, setImageError] = useState(false);
  const logoUrl = logo 
    ? (logo.includes('http') ? logo : `http://localhost:59844/${logo}`)
    : null;

  if (logoUrl && !imageError) {
    return (
      <img 
        src={logoUrl} 
        alt={name}
        style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '15px' }}
        onError={() => setImageError(true)}
      />
    );
  }
  return <div className="college-header-icon">🏛️</div>;
}

function CollegeDetails() {
  const { id } = useParams();
  const [college, setCollege] = useState(null);
  const [placementDrives, setPlacementDrives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCollegeDetails();
  }, [id]);

  const loadCollegeDetails = async () => {
    try {
      setLoading(true);
      const [collegesData, drivesData] = await Promise.all([
        collegeService.getColleges(),
        placementDriveService.GetNewPlacementDrivesByCollege(id)
      ]);
      
      const foundCollege = collegesData.find(c => c.Id === parseInt(id));
      if (foundCollege) {
        setCollege(foundCollege);
      }
      setPlacementDrives(drivesData);
    } catch (error) {
      console.error('Error loading college details:', error);
      alert('Error loading college details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getLocation = (college) => {
    if (!college) return 'Location not specified';
    const parts = [];
    if (college.Location) parts.push(college.Location);
    if (college.District?.Name) parts.push(college.District.Name);
    if (college.State?.Name) parts.push(college.State.Name);
    if (college.Country?.Name) parts.push(college.Country.Name);
    return parts.length > 0 ? parts.join(', ') : 'Location not specified';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatus = (lastDate) => {
    if (!lastDate) return 'Upcoming';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lastDateObj = new Date(lastDate);
    lastDateObj.setHours(0, 0, 0, 0);
    
    if (lastDateObj < today) {
      return 'Completed';
    } else if (lastDateObj.getTime() === today.getTime()) {
      return 'Ongoing';
    } else {
      return 'Upcoming';
    }
  };

  if (loading) {
    return (
      <div className="college-details">
        <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Loading college details...</p>
        </div>
      </div>
    );
  }

  if (!college) {
    return (
      <div className="college-details">
        <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
          <p>College not found.</p>
          <Link to="/colleges" className="back-link">← Back to Colleges</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="college-details">
      <div className="college-details-header">
        <div className="container">
          <Link to="/colleges" className="back-link">← Back to Colleges</Link>
          <div className="college-header-content">
            <div className="college-header-image">
              <CollegeHeaderImage logo={college.Logo} name={college.Name} />
            </div>
            <div className="college-header-info">
              <h1>{college.Name}</h1>
              {college.Code && <p className="college-code">Code: {college.Code}</p>}
              {college.University?.Name && <p className="college-university">🏫 {college.University.Name}</p>}
              <div className="college-meta">
                <span>📍 {getLocation(college)}</span>
                {college.Email && <span>📧 {college.Email}</span>}
                {college.MobileNo && <span>📞 {college.MobileNo}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {college.Description && (
          <div className="college-description-section">
            <h2>About This College</h2>
            <p>{college.Description}</p>
          </div>
        )}

        {college.Address && (
          <div className="college-info-section">
            <h2>Contact Information</h2>
            <p><strong>Address:</strong> {college.Address}</p>
            <p><strong>Location:</strong> {getLocation(college)}</p>
          </div>
        )}

        <div className="placement-drives-section">
          <h2>Placement Drives ({placementDrives.length})</h2>
          <p className="section-subtitle">Active placement drives for this college</p>
          
          {placementDrives.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '10px' }}>
              <p>No placement drives available for this college.</p>
            </div>
          ) : (
            <div className="drives-grid">
              {placementDrives.map(drive => {
                const status = getStatus(drive.LastDate);
                return (
                  <Link key={drive.Id} to={`/drive/${drive.Id}`} className="drive-card-link">
                    <div className="drive-card">
                      <div className="drive-image">
                        <div className="drive-image-icon">🏢</div>
                      </div>
                      <div className="drive-content">
                        <div className="drive-header">
                          <h3>{drive.Title}</h3>
                          <span className={`status-badge ${status.toLowerCase()}`}>
                            {status}
                          </span>
                        </div>
                        <div className="drive-company">
                          <strong>{drive.Company?.Name || 'Company not specified'}</strong>
                        </div>
                        <div className="drive-info">
                          <p className="drive-date">📅 {formatDate(drive.Date)}</p>
                          {drive.LastDate && (
                            <p className="drive-date">⏰ Last Date: {formatDate(drive.LastDate)}</p>
                          )}
                        </div>
                        {drive.Description && (
                          <p className="drive-description">
                            {drive.Description.length > 100 
                              ? drive.Description.substring(0, 100) + '...' 
                              : drive.Description}
                          </p>
                        )}
                        <button className="view-details-btn">View Details & Apply</button>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CollegeDetails;

